import React, { useRef, useEffect } from 'react';

import ArcGISMap from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import Extent from '@arcgis/core/geometry/Extent';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Basemap from '@arcgis/core/Basemap';
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';
import setupWidgets from '../widgets';
import popupContent from './helpers/popupContent';
import getClassBreaksRenderer from './helpers/cbr';
import setHeaderColor from './helpers/setPopupHeaderColor';

let map;
let view;

function MainMap({ lat, lng }) {
  const mapDiv = useRef(null);

  useEffect(() => {
    const basemap = new Basemap({
      baseLayers: [
        new VectorTileLayer({
          url: 'https://azmag.maps.arcgis.com/sharing/rest/content/items/28f6ec938f634223be8fc577d2a86ca5/resources/styles/root.json',
        }),
      ],
    });

    const countyBorderLayer = new FeatureLayer({
      url: 'https://geo.azmag.gov/arcgis/rest/services/maps/DemographicState2020/MapServer/16',
      definitionExpression: `NAME = 'Maricopa County'`,
    });

    const outFields = [
      'HH_blw4p80AMI',
      'TOTAL_HH_FOR_INCOME',
      'HH_blw4p50AMI',
      'MEDIAN_HOUSEHOLD_INCOME',
      'juris',
    ];

    const blockGroupsLayer = new FeatureLayer({
      url: 'https://geo.azmag.gov/arcgis/rest/services/maps/PhoenixAMITool/MapServer/1',
      outFields,
      id: 'BlockGroups',
      popupTemplate: {
        title: 'Selected Block Group',
        outFields,
        content: (e) => {
          return popupContent(e.graphic.attributes);
        },
      },
      renderer: getClassBreaksRenderer(),
    });

    const tractsLayer = new FeatureLayer({
      url: 'https://geo.azmag.gov/arcgis/rest/services/maps/PhoenixAMITool/MapServer/2',
      outFields,
      id: 'Tracts',
      visible: false,
      popupTemplate: {
        title: 'Selected Tract',
        outFields,
        content: (e) => {
          return popupContent(e.graphic.attributes);
        },
      },
      renderer: getClassBreaksRenderer(),
    });

    if (mapDiv.current) {
      map = new ArcGISMap({
        basemap,
        layers: [blockGroupsLayer, tractsLayer, countyBorderLayer],
      });

      view = new MapView({
        map,
        container: mapDiv.current,
        extent: {
          spatialReference: {
            wkid: 102100,
          },
          xmax: -12399806.870340414,
          xmin: -12546566.258166712,
          ymax: 3995302.6059680916,
          ymin: 3921234.977424507,
        },
        constraints: {
          rotationEnabled: false,
          minZoom: 8,
          snapToZoom: true,
        },
        popup: {
          alignment: 'top-center',
          collapseEnabled: false,
          actions: [],
          dockOptions: {
            buttonEnabled: false,
            breakpoint: false,
          },
        },
        ui: { components: [] },
      });

      (async () => {
        const countyBorderLayerView = await view.whenLayerView(
          countyBorderLayer
        );

        setupWidgets({
          view,
          map,
          countyBorderLayer,
          countyBorderLayerView,
        });
      })();

      var maxExtent = new Extent({
        spatialReference: {
          wkid: 102100,
        },
        xmax: -12339330.791267041,
        xmin: -12592490.735267404,
        ymax: 4134647.588827106,
        ymin: 3778756.073348334,
      });

      view.watch('popup.selectedFeature', function (feature) {
        if (feature) {
          const { HH_blw4p50AMI, HH_blw4p80AMI, TOTAL_HH_FOR_INCOME } =
            feature.attributes;
          const HHvw4p50AMIPct = HH_blw4p50AMI / TOTAL_HH_FOR_INCOME;
          const HHvw4p80AMIPct = HH_blw4p80AMI / TOTAL_HH_FOR_INCOME;

          setHeaderColor(
            HHvw4p50AMIPct >= 0.6
              ? 'rgb(168, 56, 0)'
              : HHvw4p80AMIPct >= 0.6
              ? 'rgb(255, 198, 39)'
              : 'rgb(0,0,0)'
          );
        }
      });

      view.watch('extent', function (extent) {
        if (extent) {
          var currentCenter = extent.center;
          if (!maxExtent.contains(currentCenter)) {
            var newCenter = extent.center;
            if (currentCenter.x < maxExtent.xmin) {
              newCenter.x = maxExtent.xmin;
            }
            if (currentCenter.x > maxExtent.xmax) {
              newCenter.x = maxExtent.xmax;
            }
            if (currentCenter.y < maxExtent.ymin) {
              newCenter.y = maxExtent.ymin;
            }
            if (currentCenter.y > maxExtent.ymax) {
              newCenter.y = maxExtent.ymax;
            }

            var newExtent = view.extent.clone();
            newExtent.centerAt(newCenter);
            view.extent = newExtent;
          }
        }
      });
    }
  }, []);

  return <div className="mapDiv" ref={mapDiv}></div>;
}
function getMapRef() {
  return { map, view };
}

export { getMapRef, MainMap };
